import type {CSSProperties} from 'react';
import {WEDGE_AVATAR_ONBOARD as A} from '@territory/tokens';
import {radius} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeAvatarOnboardLook =
  | 'avob-theme' | 'avob-theme-on'
  | 'avob-guide' | 'avob-guide-blip-on' | 'avob-guide-scout-on'
  | 'avob-go' | 'avob-go-block'
  | 'avob-link' | 'avob-back' | 'avob-back-off'
  | 'avob-bubble'
  | 'avob-pill' | 'avob-pill-quiet' | 'avob-pick' | 'avob-pick-on' | 'avob-skip';

export type WedgeAvatarOnboardSelectVariant = 'wedge-avob-select' | 'wedge-avob-select-on';
export const isWedgeAvatarOnboardSelectVariant = (variant: unknown): variant is WedgeAvatarOnboardSelectVariant => variant === 'wedge-avob-select' || variant === 'wedge-avob-select-on';

export type WedgeAvatarOnboardFieldVariant = 'wedge-avob-field' | 'wedge-avob-area';
export const isWedgeAvatarOnboardFieldVariant = (variant: unknown): variant is WedgeAvatarOnboardFieldVariant => variant === 'wedge-avob-field' || variant === 'wedge-avob-area';

export const isWedgeAvatarOnboardLook = (look: string | undefined): look is WedgeAvatarOnboardLook => look !== undefined && look.startsWith('avob-');

/* Released avatar onboarding (prep-onboard-avatar AvatarOnboarding, the pick phase and the stage chrome). The Appearance pill is a borderless
   pill with 7px by 18px of padding and 12.5px semibold type with a 1px track; the chosen pill takes the accent fill and the on-accent ink, and
   both ease their fill and ink over 180ms. A guide card is a flexible column of 16px by 14px of padding with a 16px radius: a hairline separator
   edge at rest, a 2px accent edge on the soft accent fill for Blip, a 2px pink edge on the soft pink fill for Scout. The go chip is the gold
   pill (50px tall at least, 14px by 22px of padding, 15px bold type) that rides the shipped avob-chip class; the block form fills the line at
   16px. The text links ride the shipped avob-textlink class and paint nothing inline. Back eases to 0.35 opacity when there is no step to
   go back to. The bubble is the speech bubble drawn by the shipped avob-bubble class on a native button: it keeps the border box the wedge scope gives a div, the
   inherited face and alignment of the div it replaces, and lays its text out from the top like a block. Each reads the exact legacy roles, and
   the declaration order is the order of the source. The caller owns the labels, the handlers and the shipped classes.

   The step chips (the avatar controls zone). An option pill is a 44px pill with a 1.5px accent edge and accent 15px semibold ink on no fill;
   the quiet pill takes the separator edge and the secondary ink. A pick chip (sub-industry, why) is the same pill at 500 weight with a separator
   edge and the secondary ink; once chosen it takes the soft accent fill (the caller names the edge and ink hue for the sub-industry groups). The
   skip chip is the 50px pill on the alternate fill with a 1.5px bold separator edge, the primary ink and 700 type; the quiet pill's separator edge
   color lands over the bold edge, as the source order paints it. The go chip above is the
   gold form. The caller layers the pending pick (accent edge, ink and soft fill), the disabled dim, the directional top margin and the
   entrance stagger over these. The industry select is a bare native select, so the iOS wheel picker stays: 50px tall at least, a 12px radius,
   a 1.5px edge that turns gold once an industry is chosen, 16px type, and no outline of its own.

   The composer fields (the typed answer, the tel field and the inline editor field). One bare native input or textarea that grows in its flex
   row (flex 1, min width 0): a 12px radius, a 1px separator edge, the alternate fill, the primary ink (also as the WebKit text fill), 12px by 14px
   of padding, 16px type, a 44px floor and the inherited face. The area form takes no resize handle. `rows`, the type and the key handlers stay with
   the caller. */
const THEME_PADDING = '7px 18px', THEME_FONT = 12.5, THEME_TRACK = 1, THEME_EASE = 180;
const GUIDE_PADDING = '16px 14px', GUIDE_GAP = 6;
const GO_MIN = 50, GO_PADDING = '14px 22px', GO_FONT = 15, GO_BLOCK_FONT = 16;
const BACK_OFF_OPACITY = 0.35;
const PILL_MIN = 44, PILL_PADDING = '10px 16px', PILL_FONT = 15, PILL_EDGE = 1.5, SKIP_MIN = 50, SKIP_PADDING = '14px 22px';
const SELECT_MIN = 50, SELECT_PADDING = '12px 14px', SELECT_FONT = 16;

export function wedgeAvatarOnboardSelectStyle(variant: WedgeAvatarOnboardSelectVariant): CSSProperties {
  const r = wedgeExactRole;
  return {
    width: '100%', boxSizing: 'border-box', minHeight: SELECT_MIN, borderRadius: radius.lg, border: `${PILL_EDGE}px solid ${variant === 'wedge-avob-select-on' ? r('gold') : r('separator')}`,
    background: r('surfaceAlt'), color: r('label'), WebkitTextFillColor: r('label'), padding: SELECT_PADDING, fontSize: SELECT_FONT, fontFamily: r('font'), outline: 'none',
  };
}

export function wedgeAvatarOnboardFieldStyle(variant: WedgeAvatarOnboardFieldVariant): CSSProperties {
  const r = wedgeExactRole;
  return {
    flex: 1, minWidth: 0, borderRadius: radius.lg, border: `1px solid ${r('separator')}`, background: r('surfaceAlt'), color: r('label'), WebkitTextFillColor: r('label'),
    padding: '12px 14px', fontSize: SELECT_FONT, minHeight: 44, fontFamily: 'inherit', ...(variant === 'wedge-avob-area' ? {resize: 'none' as const} : {}),
  };
}

// The shipped chip object, then the keys each use overrides, in the order of the source.
const pill = (): CSSProperties => ({
  minHeight: PILL_MIN, padding: PILL_PADDING, borderRadius: radius.pill, border: `${PILL_EDGE}px solid ${wedgeExactRole('accent')}`, background: 'transparent', color: wedgeExactRole('accent'),
  fontSize: PILL_FONT, fontWeight: 600, cursor: 'pointer',
});

export function wedgeAvatarOnboardStyle(look: WedgeAvatarOnboardLook): CSSProperties {
  const r = wedgeExactRole;
  const ease = (props: string[]) => props.map(p => `${p} ${THEME_EASE}ms ${r('ease')}`).join(', ');
  switch (look) {
    case 'avob-theme':
    case 'avob-theme-on': {
      const on = look === 'avob-theme-on';
      return {
        border: 0, borderRadius: radius.pill, padding: THEME_PADDING, fontSize: THEME_FONT, fontWeight: 600, letterSpacing: THEME_TRACK, cursor: 'pointer',
        background: on ? r('accent') : 'transparent', color: on ? r('onAccent') : r('labelSec'), transition: ease(['background', 'color']),
      };
    }
    case 'avob-guide':
    case 'avob-guide-blip-on':
    case 'avob-guide-scout-on': {
      const blip = look === 'avob-guide-blip-on';
      const on = look !== 'avob-guide';
      return {
        flex: 1, borderRadius: radius.card, border: on ? `2px solid ${blip ? r('accent') : A.scoutHue}` : `1px solid ${r('separator')}`,
        background: on ? (blip ? r('accentTint') : A.scoutHueBg) : 'transparent', padding: GUIDE_PADDING, cursor: 'pointer',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: GUIDE_GAP, transition: ease(['border-color', 'background']),
      };
    }
    case 'avob-go':
    case 'avob-go-block':
      return {
        minHeight: GO_MIN, padding: GO_PADDING, borderRadius: radius.pill, border: 0, background: r('gold'), color: r('onGold'),
        fontSize: look === 'avob-go-block' ? GO_BLOCK_FONT : GO_FONT, fontWeight: 700, cursor: 'pointer', ...(look === 'avob-go-block' ? {width: '100%'} : {}),
      };
    case 'avob-pill': return pill();
    case 'avob-pill-quiet': return {...pill(), borderColor: r('separator'), color: r('labelSec')};
    case 'avob-pick': return {...pill(), borderColor: r('separator'), color: r('labelSec'), background: 'transparent', fontWeight: 500};
    case 'avob-pick-on': return {...pill(), borderColor: r('accent'), color: r('accent'), background: r('accentTint'), fontWeight: 500};
    case 'avob-skip': return {...pill(), background: r('surfaceAlt'), border: `${PILL_EDGE}px solid ${r('separatorBold')}`, color: r('label'), minHeight: SKIP_MIN, padding: SKIP_PADDING, fontWeight: 700, borderColor: r('separator')};
    case 'avob-link':
      return {};
    case 'avob-back':
      return {opacity: 1};
    case 'avob-back-off':
      return {opacity: BACK_OFF_OPACITY};
    case 'avob-bubble':
      return {
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', fontFamily: 'inherit', fontWeight: 'inherit',
        letterSpacing: 'inherit', textAlign: 'inherit', whiteSpace: 'normal',
      };
  }
}
